// Word export for a monthly stipend report.
//
// Same technique as export-monthly-report.ts: a Word-compatible HTML document
// served with the msword MIME type. Word/Docs/LibreOffice open it natively and
// it needs no extra dependency, unlike building a true OOXML .docx package.

import type { MonthlyReport } from '@/types/monthly'

export interface StipendExportEmployee {
  name?: string | null
  employeeId?: string | null
  email?: string | null
  department?: string | null
  position?: string | null
  role?: string | null
  assignedHrName?: string | null
}

const escapeHtml = (value: unknown) => String(value ?? '-').replace(/[&<>'"]/g, char => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
}[char] || char))

const rupee = (value: number | null | undefined) => `₹${Math.round(value || 0).toLocaleString('en-IN')}`

function monthLabel(month: string): string {
  const [year, monthNumber] = String(month || '').split('-')
  const date = new Date(Number(year), Number(monthNumber) - 1, 1)
  return Number.isNaN(date.getTime())
    ? String(month || '-')
    : date.toLocaleString('en-US', { month: 'long', year: 'numeric' })
}

function row(label: string, value: string, strong = false) {
  return `<tr>
    <td class="k">${escapeHtml(label)}</td>
    <td class="v${strong ? ' strong' : ''}">${escapeHtml(value)}</td>
  </tr>`
}

export function exportStipendReportDoc(report: MonthlyReport, employee: StipendExportEmployee = {}) {
  const label = monthLabel(report.month)
  const breakdown = report.stipendBreakdown
  const finalAmount = report.finalStipend ?? report.suggestedStipend ?? 0
  const name = employee.name || report.userName

  const ledgerRows = (breakdown?.daily || []).length === 0
    ? `<tr><td colspan="5" class="empty">No payable days were recorded for this period.</td></tr>`
    : (breakdown?.daily || []).map(day => `<tr>
        <td class="date">${escapeHtml(day.date)}</td>
        <td>${escapeHtml(day.status)}</td>
        <td class="num">${escapeHtml(day.attendanceHours)}h</td>
        <td class="num">${escapeHtml(day.dailyWorkHours)}h</td>
        <td class="num strong">${escapeHtml(day.payableHours)}h</td>
      </tr>`).join('')

  const taskRows = (report.tasks?.completedTitles || []).length === 0
    ? `<li class="muted">No tasks were completed in this period.</li>`
    : report.tasks.completedTitles.map(title => `<li>${escapeHtml(title)}</li>`).join('')

  const html = `<!DOCTYPE html>
  <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
  <head>
    <meta charset="utf-8" />
    <title>Stipend Report - ${escapeHtml(name)} - ${escapeHtml(label)}</title>
    <!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View><w:Zoom>100</w:Zoom></w:WordDocument></xml><![endif]-->
    <style>
      @page { size: A4; margin: 18mm 16mm; }
      body { margin: 0; color: #1f2937; font-family: Arial, Helvetica, sans-serif; font-size: 10pt; line-height: 1.45; }
      .top-rule { height: 7px; background: #f97316; margin-bottom: 20px; }
      .header { width: 100%; border-collapse: collapse; margin-bottom: 22px; }
      .brand-name { margin: 0; color: #111827; font-size: 19pt; font-weight: 700; letter-spacing: -.3pt; }
      .brand-subtitle { margin: 3px 0 0; color: #6b7280; font-size: 8.5pt; letter-spacing: .4pt; text-transform: uppercase; }
      .report-meta { text-align: right; vertical-align: top; }
      .badge { display: inline-block; padding: 4px 10px; color: #047857; background: #ecfdf5; border: 1px solid #a7f3d0; border-radius: 12px; font-size: 8pt; font-weight: 700; letter-spacing: .4pt; }
      .report-title { margin: 10px 0 1px; color: #111827; font-size: 12pt; font-weight: 700; }
      .report-period { margin: 0; color: #6b7280; font-size: 9pt; }
      .section-title { margin: 18px 0 9px; color: #6b7280; font-size: 8pt; font-weight: 700; letter-spacing: .8pt; text-transform: uppercase; }
      table.kv { width: 100%; border-collapse: collapse; border: 1px solid #e5e7eb; }
      table.kv td { padding: 8px 12px; border-bottom: 1px solid #e5e7eb; font-size: 9.5pt; }
      table.kv tr:last-child td { border-bottom: 0; }
      td.k { width: 48%; color: #6b7280; background: #fafafa; }
      td.v { color: #111827; font-weight: 600; }
      td.v.strong { color: #ea580c; font-size: 12pt; font-weight: 700; }
      .hero { width: 100%; border-collapse: collapse; margin-bottom: 4px; border: 1px solid #fed7aa; background: #fff7ed; }
      .hero td { padding: 14px 16px; }
      .hero-label { display: block; color: #9a3412; font-size: 8pt; font-weight: 700; letter-spacing: .5pt; text-transform: uppercase; }
      .hero-value { display: block; margin-top: 3px; color: #ea580c; font-size: 22pt; font-weight: 700; }
      .hero-note { display: block; margin-top: 2px; color: #7c2d12; font-size: 8.5pt; }
      table.grid { width: 100%; border-collapse: collapse; table-layout: fixed; }
      table.grid th { padding: 8px; color: #fff; background: #1f2937; border: 1px solid #1f2937; font-size: 8pt; font-weight: 700; text-align: left; text-transform: uppercase; }
      table.grid td { padding: 7px 8px; border-bottom: 1px solid #e5e7eb; color: #374151; font-size: 8.5pt; }
      table.grid tr:nth-child(even) td { background: #f9fafb; }
      .num { text-align: right; } .strong { font-weight: 700; color: #111827; }
      .date { font-weight: 700; color: #111827; }
      .empty { padding: 18px !important; color: #6b7280 !important; text-align: center; }
      ul { margin: 0; padding-left: 18px; } li { font-size: 9pt; margin-bottom: 2px; }
      .muted { color: #6b7280; list-style: none; margin-left: -18px; }
      .footer { margin-top: 22px; padding-top: 10px; border-top: 1px solid #e5e7eb; color: #9ca3af; font-size: 8pt; width: 100%; }
      .footer .right { text-align: right; }
    </style>
  </head>
  <body>
    <div class="top-rule"></div>
    <table class="header"><tr>
      <td><h1 class="brand-name">Sasha Infinity</h1><p class="brand-subtitle">Human Resources &amp; Stipend Management</p></td>
      <td class="report-meta">
        <span class="badge">${escapeHtml(report.finalStipend != null ? 'FINALIZED' : 'PENDING REVIEW')}</span>
        <p class="report-title">Monthly Stipend Report</p>
        <p class="report-period">${escapeHtml(label)}</p>
      </td>
    </tr></table>

    <table class="hero"><tr><td>
      <span class="hero-label">${escapeHtml(report.finalStipend != null ? 'Final Stipend' : 'Suggested Stipend')}</span>
      <span class="hero-value">${escapeHtml(rupee(finalAmount))}</span>
      <span class="hero-note">Performance tier: ${escapeHtml(report.tier)} · Overall score ${escapeHtml(report.scores?.overall ?? '-')}</span>
    </td></tr></table>

    <p class="section-title">Employee Information</p>
    <table class="kv">
      ${row('Name', String(name))}
      ${row('Employee ID', String(employee.employeeId ?? '-'))}
      ${row('Role', String(employee.role ?? '-'))}
      ${row('Department', String(employee.department ?? '-'))}
      ${row('Position', String(employee.position ?? '-'))}
      ${row('Email', String(employee.email ?? '-'))}
      ${row('Assigned HR', String(employee.assignedHrName ?? '-'))}
    </table>

    <p class="section-title">Attendance Summary</p>
    <table class="kv">
      ${row('Working days', String(report.attendance?.workingDays ?? '-'))}
      ${row('Present', String(report.attendance?.present ?? 0))}
      ${row('Work from home', String(report.attendance?.wfh ?? 0))}
      ${row('Leave', String(report.attendance?.leave ?? 0))}
      ${row('Holiday', String(report.attendance?.holiday ?? 0))}
      ${row('Absent', String(report.attendance?.absent ?? 0))}
      ${row('Incomplete days', String(report.attendance?.incomplete ?? 0))}
    </table>

    <p class="section-title">Hours</p>
    <table class="kv">
      ${row('Attendance hours', `${report.hours?.total ?? 0}h`)}
      ${row('Payable hours', `${report.hours?.payable ?? report.hours?.total ?? 0}h`)}
      ${row('Expected hours', `${report.hours?.expected ?? 0}h`)}
      ${row('Daily work logged', `${breakdown?.dailyWorkHours ?? 0}h`)}
    </table>

    <p class="section-title">Stipend Calculation</p>
    <table class="kv">
      ${row('Hourly pay', rupee(breakdown?.hourlyPay))}
      ${row('Task bonus', rupee(breakdown?.taskPay))}
      ${row('Base pay', rupee(breakdown?.basePay))}
      ${row('Performance bonus', rupee(breakdown?.performanceBonus))}
      ${breakdown?.cappedAt != null ? row('Uncapped total', rupee(breakdown.uncappedStipend)) : ''}
      ${breakdown?.cappedAt != null ? row('Monthly cap applied', rupee(breakdown.cappedAt)) : ''}
      ${row('Suggested stipend', rupee(report.suggestedStipend))}
      ${row('Final stipend', report.finalStipend != null ? rupee(report.finalStipend) : 'Pending', true)}
    </table>

    <p class="section-title">Tasks Completed (${escapeHtml(report.tasks?.completed ?? 0)})</p>
    <ul>${taskRows}</ul>

    <p class="section-title">Daily Payable Ledger</p>
    <table class="grid">
      <thead><tr><th>Date</th><th>Status</th><th>Attendance</th><th>Daily Work</th><th>Payable</th></tr></thead>
      <tbody>${ledgerRows}</tbody>
    </table>

    <table class="footer"><tr>
      <td>This report is system-generated for official HR documentation.</td>
      <td class="right">Generated on ${escapeHtml(new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }))}</td>
    </tr></table>
  </body></html>`

  const blob = new Blob(['﻿' + html], { type: 'application/msword;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${String(name).replace(/\s+/g, '_')}_Stipend_Report_${report.month}.doc`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
